import { ChevronLeft, X } from 'lucide-react';

// Shared furniture for every Game Planner wizard screen.
//
// The whole point of the wizard is that a coach never has to hunt: the title is
// always in the same place, and the primary action is always the same size in
// the same spot at the bottom. So the footer is a fixed shape, and `secondary`
// is deliberately sized to the exact height of the skip link it replaces. Swap
// one for the other and the primary button does not move.
//
// Tokens only, no inline hex. These screens are the ones taking PlanGameView out
// of the `legacy-invert` quarantine, so nothing here may depend on the inversion.

/**
 * Wide screens (unfolded iPhone Duo, iPad landscape, laptop): the screen's own
 * content on the left at phone width, `side` on the right, split down the middle
 * so nothing sits on a Duo's fold. Phones render the content alone, unchanged.
 * The left pane is `relative`, so a screen's sheets (absolute inset-0) open
 * inside it instead of across the fold.
 */
export const TwoPane = ({ wide, side, children }: { wide: boolean; side: React.ReactNode; children: React.ReactNode }) =>
  wide ? (
    <div className="flex-1 min-h-0 flex">
      <div className="relative w-1/2 min-w-0 flex flex-col">{children}</div>
      <div className="relative w-1/2 min-w-0 flex flex-col border-l border-hairline">{side}</div>
    </div>
  ) : (
    <>{children}</>
  );

/** Nav bar: back or close on the left, a step label centred, one optional action right. */
export const WizardBar = ({ label, onBack, onClose, right }: {
  label?: string;
  onBack?: () => void;
  onClose?: () => void;
  right?: React.ReactNode;
}) => (
  <div className="flex items-center gap-3 px-4 pt-4 pb-1 bg-surface">
    {onBack ? (
      <button onClick={onBack} aria-label="Back" className="p-1 -ml-1 text-ink-2 shrink-0">
        <ChevronLeft className="w-6 h-6" />
      </button>
    ) : onClose ? (
      <button onClick={onClose} aria-label="Close" className="p-1 -ml-1 text-ink-2 shrink-0">
        <X className="w-5 h-5" />
      </button>
    ) : <span className="w-6 shrink-0" />}

    <span className="flex-1 text-center text-[11px] font-semibold uppercase tracking-[0.09em] text-ink-mute">
      {label}
    </span>

    <span className="w-7 shrink-0 flex justify-end text-ink">{right}</span>
  </div>
);

/** Big plain-label title plus one line of plain English underneath. */
export const WizardHead = ({ title, sub }: { title: string; sub?: React.ReactNode }) => (
  <div className="px-5 pt-2 pb-4 bg-surface">
    <h2 className="text-[27px] font-bold tracking-[-0.028em] leading-[1.15] text-ink">{title}</h2>
    {sub && <p className="text-[14.5px] text-ink-2 mt-1.5 leading-snug">{sub}</p>}
  </div>
);

/**
 * Footer. `primary` is the one full-width action. `secondary` is either a skip
 * link (steps) or a real second button (the review card), both 38px tall so the
 * primary button lands at the same height on every screen.
 */
export const WizardFoot = ({ primaryLabel, onPrimary, secondaryLabel, onSecondary, secondaryAsButton }: {
  primaryLabel: string;
  onPrimary: () => void;
  secondaryLabel?: string;
  onSecondary?: () => void;
  secondaryAsButton?: boolean;
}) => (
  <div className="px-4 pt-3.5 pb-5 safe-bottom shrink-0">
    <button
      onClick={onPrimary}
      className="w-full py-4 rounded-control bg-ink text-surface text-base font-bold tracking-[-0.01em]"
    >
      {primaryLabel}
    </button>
    {secondaryLabel && (
      secondaryAsButton ? (
        <button
          onClick={onSecondary}
          className="w-full h-[38px] mt-2 rounded-control bg-surface border border-hairline text-ink text-[15px] font-bold"
        >
          {secondaryLabel}
        </button>
      ) : (
        <button
          onClick={onSecondary}
          className="w-full h-[38px] pt-3 text-sm font-semibold text-ink-2"
        >
          {secondaryLabel}
        </button>
      )
    )}
  </div>
);

/** A tappable list row: optional leading slot, name, trailing meta, chevron. */
export const WizardRow = ({ lead, title, sub, meta, trailing, onClick, dim, last }: {
  lead?: React.ReactNode;
  title: React.ReactNode;
  sub?: React.ReactNode;
  meta?: React.ReactNode;
  trailing?: React.ReactNode;
  onClick?: () => void;
  dim?: boolean;
  last?: boolean;
}) => (
  <button
    onClick={onClick}
    disabled={!onClick}
    className={`w-full flex items-center gap-3 px-4 py-3.5 text-left ${last ? '' : 'border-b border-hairline'} ${dim ? 'opacity-55' : ''}`}
  >
    {lead}
    <span className="flex-1 min-w-0">
      <span className="block text-base font-semibold tracking-[-0.012em] text-ink truncate">{title}</span>
      {sub && <span className="block text-[13px] text-ink-2 mt-0.5 truncate">{sub}</span>}
    </span>
    {meta && <span className="text-sm text-ink-2 shrink-0 tabular-nums">{meta}</span>}
    {trailing}
  </button>
);

/** White rounded container that list rows sit inside. */
export const WizardList = ({ children, className = '' }: { children: React.ReactNode; className?: string }) => (
  <div className={`bg-surface rounded-panel overflow-hidden ${className}`}>{children}</div>
);

/** Small muted group label above a list. */
export const WizardGroupLabel = ({ children, right }: { children: React.ReactNode; right?: React.ReactNode }) => (
  <div className="flex items-baseline justify-between px-1 mb-2 mt-4 first:mt-0">
    <span className="text-sm font-semibold text-ink-2">{children}</span>
    {right && <span className="text-[13px] text-ink-2 tabular-nums">{right}</span>}
  </div>
);

/**
 * A select rendered as a row. Used for the defense fill mode and the auto-lineup
 * mode, replacing the chip rails that could only ever show three of nine options.
 * The native <select> is invisible on top so iOS gives us its own wheel picker.
 * (A native <option> can only ever show its own label — no room for a second
 * line — so `description`, when the caller passes per-option descriptions,
 * renders as small text below the row instead, showing the CURRENT selection's
 * description. StepOrder surfaces its mode description in the screen header
 * instead and does not pass this.)
 */
export const WizardSelect = ({ label, value, options, onChange }: {
  label: string;
  value: string;
  options: { key: string; label: string; description?: string }[];
  onChange: (key: string) => void;
}) => {
  const current = options.find(o => o.key === value);
  return (
    <div>
      <div className="relative bg-surface border border-hairline rounded-control px-4 py-3 flex items-center gap-3">
        <span className="text-sm text-ink-2 shrink-0">{label}</span>
        <span className="flex-1 text-right text-[15.5px] font-bold text-ink truncate">
          {current?.label ?? value}
        </span>
        <span className="text-ink-mute text-xs shrink-0">▾</span>
        <select
          value={value}
          onChange={e => onChange(e.target.value)}
          aria-label={label}
          className="absolute inset-0 w-full h-full opacity-0"
        >
          {options.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
        </select>
      </div>
      {current?.description && (
        <p className="text-[11.5px] text-ink-2 mt-1.5 px-1">{current.description}</p>
      )}
    </div>
  );
};
